import { toast } from "sonner";
import { t } from "copy/t";
import { capture } from "utils/posthog";

// Copy Link icon (square behind square)
const CopyLinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5.5" y="5.5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10.5 5.5V3C10.5 2.17157 9.82843 1.5 9 1.5H3C2.17157 1.5 1.5 2.17157 1.5 3V9C1.5 9.82843 2.17157 10.5 3 10.5H5.5" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

// Share icon (arrow out of box)
const ShareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 10V2M8 2L5 5M8 2L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 10V12C3 13.1046 3.89543 14 5 14H11C12.1046 14 13 13.1046 13 12V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export default function SingleView({ gif, backgroundColor, isModal = false, channelSlug, shareKey }) {
  // A locked photo's link carries its per-photo key (`?k=`); a copied or shared
  // link keeps it so the recipient opens the photo instead of the password gate.
  const buildShareUrl = () =>
    `${window.location.origin}/ch/${channelSlug}/${gif?.id}${
      shareKey ? `?k=${encodeURIComponent(shareKey)}` : ""
    }`;

  // Measurement fires only on a CONFIRMED share (clipboard write resolved /
  // the native share sheet completed) — never on a tap that errored or was
  // cancelled. Identifiers only: the slug, and how it was shared.
  const handleCopyLink = async () => {
    const shareUrl = buildShareUrl();
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("toast.gallery.copied"));
      capture("gif_shared", { channel_slug: channelSlug, method: "copy_link" });
    } catch (err) {
      console.error("Failed to copy link:", err);
      toast.error(t("toast.gallery.copyFailed"));
    }
  };

  const handleShare = async () => {
    const shareUrl = buildShareUrl();

    if (navigator.share) {
      try {
        await navigator.share({ url: shareUrl });
        capture("gif_shared", { channel_slug: channelSlug, method: "native" });
        return;
      } catch (err) {
        // User cancelled - exit silently
        if (err.name === 'AbortError') return;
        // Share failed - fall through to clipboard fallback
        console.error("Share failed:", err);
      }
    }

    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("toast.gallery.copied"));
      capture("gif_shared", { channel_slug: channelSlug, method: "copy_link" });
    } catch (err) {
      console.error("Failed to copy URL:", err);
      toast.error(t("toast.gallery.copyFailed"));
    }
  };

  const poster = gif?.image_urls?.original || gif?.image_url;
  // The cap leaves room for the header (64px), the action buttons and the fixed
  // footer (170px together). Phones get it too, in dynamic viewport units so a
  // collapsing browser toolbar cannot push the buttons under the footer; the
  // media is letterboxed (object-contain) rather than stretched to fit.
  const mediaSizeClass = isModal
    ? "max-h-[calc(100dvh-170px)] object-contain sm:max-h-[calc(100vh-170px)]"
    : "max-h-[calc(100dvh-64px-170px)] object-contain sm:max-h-[calc(100vh-64px-170px)]";

  return (
    <div
      className={`flex flex-col ${
        !isModal ? "h-full pb-[80px] sm:pb-0 sm:h-auto" : ""
      }`}
      style={!isModal ? { background: backgroundColor } : undefined}
    >
      {/* Mobile: full width layout */}
      {/* Desktop: centered inline-flex container that shrink-wraps to image width */}
      <div className="w-full sm:flex sm:justify-center">
        <div className="w-full sm:w-auto sm:inline-flex sm:flex-col">
          {/* Story video plays inline; a video whose mp4 is not available
              (not landed, purged, old file) falls back to the poster image. */}
          {gif?.is_video && gif?.video_url ? (
            <video
              src={gif.video_url}
              poster={poster}
              controls
              playsInline
              muted
              loop
              preload="metadata"
              aria-label="Play video"
              className={`w-full sm:w-auto sm:h-auto sm:rounded-lg ${mediaSizeClass}`}
            />
          ) : (
            <img
              src={poster}
              className={`w-full sm:w-auto sm:h-auto sm:rounded-lg ${mediaSizeClass}`}
              alt="Shared photo"
            />
          )}

          {/* Action buttons - same width as image on desktop */}
          <div className="flex gap-2 px-4 py-4 sm:px-0 w-full">
            {/* Copy Link button */}
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex-1 flex items-center justify-center gap-[6px] px-6 py-5 rounded-[16px] backdrop-blur text-white"
              style={{
                fontFamily: "'Geist Mono', monospace",
                border: '1px solid rgba(255, 255, 255, 0.24)',
                backgroundColor: 'rgba(255, 255, 255, 0.01)'
              }}
            >
              <CopyLinkIcon />
              <span className="font-bold text-[12px] uppercase leading-none">
                Copy Link
              </span>
            </button>

            {/* Share button */}
            <button
              type="button"
              onClick={handleShare}
              className="flex-1 flex items-center justify-center gap-[6px] px-6 py-5 rounded-[16px] bg-white border border-white/[0.08]"
              style={{ fontFamily: "'Geist Mono', monospace", color: backgroundColor }}
            >
              <ShareIcon />
              <span className="font-bold text-[12px] uppercase leading-none">
                Share
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
